import { defineStore } from 'pinia'
import { useFlag } from '@unleash/proxy-client-vue'
import type { ISbRichtext } from 'storyblok-js-client'
import {
  DEMO_SPACE_THEME_CONTENT_URL,
  DEMO_SPACE_TOUR_HINTS_CONTENT_URL,
} from '@/constants/demo'
import { client } from '@/services/client'
import type { StoryContent, DemoSpaceTourContent } from '@/types'
import type { AssetContentRecord } from '@/types/content'
import createContentUrl from '@/utils/createContentUrl'
import type { AvailableButtonVariants } from '@storyblok/design-system'
import type {
  FeatureUpdate,
  LinkComponent,
  Logo,
} from '@/components/Auth/Signup/types'
import type {
  TabInfo,
  FormOption,
  PlanCardData,
} from '@/components/ContentManager/NewSpacePage/types'

export type Cookie = {
  _uid: string
  name: string
  type: string
  domain: string
  storage: string
  duration: string
  component: string
  description: string
  _editable: string
}

export type DemoThemesStoryContent = {
  _uid: string
  themes: DemoTheme[]
  component: 'demo_themes'
}

export type Step = {
  _uid: string
  label: string
  completed: boolean
  component: string
}

export type NewSpaceStoryContent = {
  _uid: string
  tabs: TabInfo[]
  select_plan_header_title: string
  select_plan_header_description: string
  component: string
  enterprise_cards: PlanCardData[]
  form_options: FormOption[]
  plans_cards: PlanCardData[]
  stepper: Step[]
  header_title: string
  header_description: string
  yearly_billing_label: string
  enterprise_headline: ISbRichtext
  enterprise_form_button_label: string
  enterprise_form_button_color: AvailableButtonVariants
  enterprise_form_observation: string
  sales_responsible_image: AssetContentRecord
  sales_responsible_name: string
  sales_responsible_role: string
}

export type DemoTheme = {
  _uid: string
  image: AssetContentRecord
  title: string
  caption: string
  spaceId: string
  component: 'theme'
  preview_url: object
}

export type LabsFeatureContent = {
  title: string
  image: AssetContentRecord
  story_slug: string
  not_available_tag_label: string
  available_tag_label: string
  enabled_tag_label: string
  short_description: string
  border_color: string
  background_color: string
  backend_feature_key: string
  backend_app_slug: string
  is_available: boolean
  body: Record<string, unknown>[]
  app_id?: number | undefined
  frontend_feature_route: string
  enable_feature_text: string
  backend_disclaimer_key: string
  notify_user_button_label: string
  enabled_button_label: string
}

export type LabsFeatureStory = {
  _uid: string
  content: LabsFeatureContent
  component: string
  slug: string
}

export type QuickstartCard = {
  _uid: string
  link: string
  icons: {
    id: number
    alt?: string
    name?: string
    focus?: string
    title?: string
    source?: string
    filename: string
    copyright: string
    fieldtype: string
    meta_data?: Record<string, unknown>
  }[]
  title: string
  video?: string
  component: string
  icon_text?: string
  full_width: boolean
  description: ISbRichtext
  video_title: string
}

export type QuickstartContent = {
  _uid: string
  link: string
  type: string
  cards: QuickstartCard[]
  component: string
  demo_cards: QuickstartCard[]
  footer_text: string
  editor_cards: QuickstartCard[]
}

export type SignupContent = {
  _uid: string
  component: string
  content: {
    feature_updates: FeatureUpdate[]
    new_logos: Logo[]
    footer_links: LinkComponent[]
  }
}

export type State = {
  cookies: StoryContent
  demos: DemoThemesStoryContent
  newSpaceStoryContent: NewSpaceStoryContent
  hints: DemoSpaceTourContent[]
  labsFeatures: LabsFeatureContent[]
  quickstartContent: QuickstartContent
  signupContent: SignupContent
}

export const useContent = defineStore('content', {
  state: (): State => ({
    cookies: {} as StoryContent,
    demos: {} as DemoThemesStoryContent,
    newSpaceStoryContent: {} as NewSpaceStoryContent,
    hints: [] as DemoSpaceTourContent[],
    labsFeatures: [] as LabsFeatureContent[],
    quickstartContent: {} as QuickstartContent,
    signupContent: {} as SignupContent,
  }),
  getters: {
    getCookieSettings: (state: State): StoryContent => state.cookies,
    getDemoDetails: (state: State): DemoThemesStoryContent => state.demos,
    getNewSpaceStoryContent: (state: State): NewSpaceStoryContent =>
      state.newSpaceStoryContent,
    getDemoSpaceHints: (state: State): DemoSpaceTourContent[] => state.hints,
    getLabsFeatures: (state: State): LabsFeatureContent[] => state.labsFeatures,
    getQuickstartContent: (state: State): QuickstartContent =>
      state.quickstartContent,
    getSignupContent: (state: State): SignupContent => state.signupContent,
  },
  actions: {
    setCookieSettings(cookies: StoryContent): void {
      this.cookies = cookies
    },
    setDemoDetails(demos: DemoThemesStoryContent): void {
      this.demos = demos
    },
    setDemoSpaceTourHints(hints: DemoSpaceTourContent[]): void {
      this.hints = hints
    },
    setNewSpaceStoryContent(story: NewSpaceStoryContent): void {
      this.newSpaceStoryContent = story
    },
    setQuickstartContent(content: QuickstartContent): void {
      this.quickstartContent = content
    },
    setLabsFeatures(features: LabsFeatureContent[]): void {
      this.labsFeatures = features
    },
    setSignupContent(content: SignupContent): void {
      this.signupContent = content
    },
    async loadCookieSettings(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/general/cookie-settings?${
          createContentUrl().urlParams
        }`,
      )

      return this.setCookieSettings(data.story.content)
    },
    async loadDemoDetails(): Promise<DemoThemesStoryContent> {
      const { data } = await client.get(
        `${DEMO_SPACE_THEME_CONTENT_URL}${createContentUrl().urlParams}`,
      )

      this.setDemoDetails(data.story.content)

      return data.story.content
    },
    async loadNewSpaceStoryContent(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/general/create-space-2025-pricing-model?${
          createContentUrl().urlParams
        }`,
      )

      this.setNewSpaceStoryContent(data.story.content)
    },
    async loadDemoSpaceTourHints(): Promise<void> {
      const { data } = await client.get(
        `${DEMO_SPACE_TOUR_HINTS_CONTENT_URL}${createContentUrl().urlParams}`,
      )

      this.setDemoSpaceTourHints(data?.story?.content?.DemoSpaceTourHint || [])
    },
    async loadLabsFeaturesContent(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories?starts_with=innovation-labs${
          createContentUrl().urlParams
        }`,
      )

      const cards = data?.stories?.map((story: LabsFeatureStory) => {
        return { ...story.content, story_slug: story.slug }
      })

      this.setLabsFeatures(cards || [])
    },

    async loadLabsFeatureContent(slug: string): Promise<LabsFeatureContent> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/innovation-labs/${slug}?${
          createContentUrl().urlParams
        }&resolve_relations=ContentTestimonials.quotes`,
      )

      return {
        ...data.story.content,
        title: data.story.content?.title || data.story.name,
      }
    },

    async loadQuickstartContent(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/get-started?${
          createContentUrl().urlParams
        }`,
      )

      return this.setQuickstartContent(data.story.content)
    },

    async loadSignupContent(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/app-signup?resolve_relations=app_ui_quote.quote${
          createContentUrl().urlParams
        }`,
      )

      this.setSignupContent(data.story)
    },
  },
})
